import {
    AssistantReservation,
    AssistantReservationTimeState,
    AssistantServiceOrderState,
} from '@mews-commander/data-access';
import { IconName } from '@optimus-web/icons';
import { useResolveRoute } from '@mews/routing';
import { RouteName, TextKey } from '@mews-commander/enums';
import { Icon, Stack, Tag, TagVariant } from '@optimus-web/core';
import { LocalizedEnum, LocalizedText, useLocalizeText, lt } from '@mews/framework';
import { getFullName } from '@mews-commander/utils';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { navigateInBaseModal, showRouteInModal } from '../../../app/actions/common';
import { highlight } from '../../utils/highlight';
import { stringifyDateRange } from '../../utils/array';
import { ResultListProps } from '../results/ListTitle';
import { StyledLeftIcon } from '../styles';
import { AssistantSearchType, SearchType } from '../../assistantSearchTypes';
import { AssistantSearchListItemButton } from '../results/searchListItemButton';
import { AssistantFilterChips } from '../../assistantSearchTypeFilters';
import { DescriptionItem, SearchItemDescription } from '../components/SearchItemDescription';
import { NEW_RESERVATION_BUTTON_DATA_TEST_ID } from '../../../newReservation/constants';
import { AssistantSearchListItemProps } from './useAssistantSearchResultListMap';
import { useLocalizedDate } from './useLocalizedDate';

interface ReservationListProps extends ResultListProps {
    reservationsWithDKReady: string[];
    reservations: AssistantReservation[];
    selectedFilter?: AssistantSearchType | AssistantFilterChips;
}

const getCustomerBookerAndCompanionNames = ({
    customer,
    companion,
    booker,
}: AssistantReservation): {
    customer: string;
    companion: string;
    booker: string;
} => {
    const customerFullName = getFullName(customer);
    const firstCompanion = companion?.[0];
    const companionFullName = firstCompanion ? getFullName(firstCompanion) : '';
    const bookerFullName = booker?.lastName ? getFullName(booker) : '';
    return {
        customer: customerFullName,
        companion: companionFullName,
        booker: bookerFullName,
    };
};

const useFormattedReservationDates = ({ startUtc, endUtc }: AssistantReservation) => {
    const localizedStartDate = useLocalizedDate(startUtc);
    const localizedEndDate = useLocalizedDate(endUtc);
    return stringifyDateRange([localizedStartDate, localizedEndDate]);
};

interface PrimaryTextProps {
    confirmationNumber: string;
    searchTerm: string;
}

interface SecondaryTextProps {
    reservation: AssistantReservation;
    searchTerm: string;
}

const PrimaryText = ({ searchTerm, confirmationNumber }: PrimaryTextProps) => {
    const name = highlight(confirmationNumber, searchTerm);
    return <span>{name}</span>;
};

const SecondaryText = ({ reservation, searchTerm }: SecondaryTextProps) => {
    const names = getCustomerBookerAndCompanionNames(reservation);
    const dates = useFormattedReservationDates(reservation);
    const room = highlight(reservation.assignedResourceName ?? '', searchTerm);
    const localizeText = useLocalizeText();
    const companionPrefix = localizeText({ textKey: TextKey.AssistantCompanion });
    const bookerPrefix = localizeText({ textKey: TextKey.Booker });

    const customer: DescriptionItem = { value: highlight(names.customer, searchTerm) };
    const companion: DescriptionItem = { value: highlight(names.companion, searchTerm), prefix: companionPrefix };
    const booker: DescriptionItem = { value: highlight(names.booker, searchTerm), prefix: bookerPrefix };
    const reservationDates: DescriptionItem = { value: dates };
    const reservationRoom: DescriptionItem = { value: room };

    const descriptions: DescriptionItem[] = [
        customer,
        ...(names.booker ? [booker] : []),
        ...(names.companion ? [companion] : []),
        reservationDates,
        ...(reservation.assignedResourceName ? [reservationRoom] : []),
    ];

    return <SearchItemDescription descriptions={descriptions} />;
};

interface RightItemProps {
    showDKReadyTag?: boolean;
    timeState?: AssistantReservationTimeState;
    serviceOrderState: AssistantServiceOrderState;
}

const tagVariants: Partial<Record<AssistantReservationTimeState, TagVariant>> = {
    [AssistantReservationTimeState.ToBeCheckedIn]: 'denim',
    [AssistantReservationTimeState.Future]: 'basicSubtle',
    [AssistantReservationTimeState.CheckInMissed]: 'tangerine',
    [AssistantReservationTimeState.CheckedIn]: 'basicBold',
    [AssistantReservationTimeState.ToBeCheckedOut]: 'denim',
    [AssistantReservationTimeState.CheckOutMissed]: 'tangerine',
    [AssistantReservationTimeState.Past]: 'basicSubtle',
};

const RightItem = ({ showDKReadyTag, timeState, serviceOrderState }: RightItemProps) => {
    if (!timeState) {
        return null;
    }

    const isCanceled: boolean = serviceOrderState === AssistantServiceOrderState.Canceled;

    const variant = isCanceled ? 'basicSubtle' : tagVariants[timeState];

    return (
        <Stack spacing="200">
            <Tag variant={variant}>
                {isCanceled ? (
                    <LocalizedEnum prefix="ServiceOrderState" value={serviceOrderState} />
                ) : (
                    <LocalizedEnum prefix="ReservationTimeState" value={timeState} />
                )}
            </Tag>
            {showDKReadyTag && <Tag variant="lavender">{lt('DigitalKeyReady')}</Tag>}
        </Stack>
    );
};

export const useReservationList = ({
    reservationsWithDKReady,
    reservations,
    searchTerm,
    selectedFilter,
}: ReservationListProps): AssistantSearchListItemProps[] => {
    const enterpriseId = useCurrentEnterpriseId();
    const resolveRoute = useResolveRoute();
    const list: Array<AssistantSearchListItemProps> = [];
    const plusIcon = <Icon size="small" value={IconName.PlusSimple} />;

    reservations.forEach((reservation) => {
        const showDKReadyTag: boolean = reservationsWithDKReady.includes(reservation.id);
        const rightItem = (
            <RightItem
                showDKReadyTag={showDKReadyTag}
                timeState={reservation.timeState}
                serviceOrderState={reservation.serviceOrderState}
            />
        );
        const url = resolveRoute(RouteName.ReservationDetailStatus, { reservationId: reservation.id });
        list.push({
            itemId: reservation.id,
            primaryText: <PrimaryText confirmationNumber={reservation.confirmationNumber} searchTerm={searchTerm} />,
            secondaryText: <SecondaryText reservation={reservation} searchTerm={searchTerm} />,
            rightItem,
            leftIcon: <StyledLeftIcon size="small" value={IconName.Reservations} />,
            routeAction: navigateInBaseModal(url),
            searchType: SearchType.Reservation,
        });
    });

    if (reservations.length > 0 && selectedFilter === SearchType.Reservation) {
        list.push({
            itemId: AssistantSearchListItemButton.NewReservation,
            primaryText: <LocalizedText textKey={TextKey.ReservationNew} />,
            leftIcon: plusIcon,
            'data-test-id': NEW_RESERVATION_BUTTON_DATA_TEST_ID,
            routeAction: showRouteInModal(RouteName.ReservationCreate, { query: { HotelId: enterpriseId } }),
        });
    }
    return list;
};
